import { savedStoryActivity, savedStoryDisplayTitle } from '../util/studioSavedActivity';

type RecordData = Record<string, any>;
type ReadStories = (input: { userId: string; storyIds: string[] }) => Promise<{ stories: RecordData[] }>;

const unavailable = () => new Error('This chapter is unavailable. Refresh the story and try again.');
const mode = (story: RecordData) => story.creationMode === 'gambit' || story.studioSourceId ? 'gambit' : 'classic';

/** Use the deployed, authenticated Classic reader while Studio's service is staged.
 * Media URLs come from that reader, never from the sidebar cache or raw S3 URLs.
 */
export async function readSavedStudioChapters(userId: string, storyId: string, chapterId: string, read: ReadStories) {
  if (!userId || !storyId || !chapterId) throw unavailable();
  const first = await read({ userId, storyIds: [storyId] });
  const root = first.stories?.find(story => story.storyId === storyId);
  if (!root || root.chapterDeletionLock || (root.userId && root.userId !== userId)) throw unavailable();
  const ids: string[] = [...new Set<string>([storyId, ...(Array.isArray(root.chapterIds) ? root.chapterIds.filter((id: unknown) => typeof id === 'string') : [])])];
  if (!ids.includes(chapterId)) throw unavailable();
  const records = new Map<string, RecordData>([[storyId, root]]);
  // The existing detail API uses DynamoDB BatchGet; stay below its 100-key cap.
  for (let offset = 1; offset < ids.length; offset += 50) {
    const pageIds = ids.slice(offset, offset + 50);
    const page = await read({ userId, storyIds: pageIds });
    for (const story of page.stories || []) {
      if (pageIds.includes(story.storyId) && !story.chapterDeletionLock && (!story.userId || story.userId === userId)) records.set(story.storyId, story);
    }
  }
  const selected = records.get(chapterId);
  if (!selected || selected.error) throw unavailable();
  const summary = (story: RecordData, index: number) => ({ storyId: story.storyId,
    title: savedStoryDisplayTitle({ ...story, storyName: story.chapterTitle || story.storyName || `Chapter ${index + 1}` }),
    chapterNumber: story.chapterNumber || index + 1, pending: savedStoryActivity(story).working,
    pendingVideoCreation: !!story.pendingVideoCreation, pendingFFmpeg: !!story.pendingFFmpeg,
    incomplete: savedStoryActivity(story).incomplete, mode: mode(story) });
  const images = selected.imageUrls || [];
  const scenes = selected.sceneData || selected.scenes || [];
  return { rootStoryId: storyId, chapters: ids.flatMap((id, index) => records.has(id) ? [summary(records.get(id)!, index)] : []),
    chapter: { ...summary(selected, ids.indexOf(chapterId)), narrative: selected.narrative || [],
      thumbnailUrl: selected.thumbnailUrl, createDate: selected.createDate,
      storyMode: selected.storyMode, storyLength: selected.storyLength, pendingVideoCreation: !!selected.pendingVideoCreation,
      pendingFFmpeg: !!selected.pendingFFmpeg, imageUrls: images, audioUrl: selected.audioUrl || selected.audioUrls?.[0],
      imageVideoUrl: selected.imageVideoUrl, animatedVideoUrl: selected.animatedVideoUrl,
      scenes: images.map((_: string, index: number) => { const scene = scenes[index] || {}; return {
        title: scene.title || `Scene ${index + 1}`, description: scene.sceneBrief || scene.description || '',
        duration: scene.duration ?? selected.sceneDurations?.[index], startSec: scene.startSec, endSec: scene.endSec,
      }; }),
      videoPrompts: images.map((_: string, index: number) => selected.videoPrompts?.[index] || scenes[index]?.videoPrompt),
    } };
}
